import { InfiniteData } from 'react-query';
import { Assignee, GetRfiTypesResponse, RfiType } from '../../../sdk';

type ReviewersField = {
  coReviewers?: Assignee[];
  optionalReviewers?: Assignee[];
};

type WithNormalizedReviewers<T> = Omit<T, keyof ReviewersField> & ReviewersField;

/**
 * BE returns both `coReviewers` and `optionalReviewers` always.
 * Prefer `optionalReviewers` as the canonical field downstream and clear `coReviewers`
 * so consumers don't drift between the two names. Undefined values are dropped on JSON
 * serialization and asserted via `expect(...).toBeUndefined()` in callers.
 */
export const normalizeRfiTypeReadPayload = <T extends ReviewersField>(
  rfiType: T,
): WithNormalizedReviewers<T> => {
  const reviewers = rfiType.optionalReviewers ?? rfiType.coReviewers;
  return { ...rfiType, coReviewers: undefined, optionalReviewers: reviewers };
};

/**
 * Flag-gated mutex: when `isOptionalReviewersEnabled` is true the outgoing payload
 * carries `optionalReviewers` only; when false it carries `coReviewers` only. Never
 * both — the server returns 400 if both are present.
 */
export const normalizeRfiTypeWritePayload = <T extends ReviewersField>(
  payload: T,
  isOptionalReviewersEnabled: boolean,
): WithNormalizedReviewers<T> => {
  const reviewers = payload.optionalReviewers ?? payload.coReviewers;
  if (!reviewers) {
    return { ...payload, coReviewers: undefined, optionalReviewers: undefined };
  }
  return isOptionalReviewersEnabled
    ? { ...payload, coReviewers: undefined, optionalReviewers: reviewers }
    : { ...payload, coReviewers: reviewers, optionalReviewers: undefined };
};

/**
 * Default rfi type should be 1st in the table.
 * the rest of the elements should be sorted alphabetically
 *
 * This helper sort all types alphabetically and put the default 1st.
 */
export const sortRfiTypesList = (RfiTypes: RfiType[]) => {
  let defaultRfiType: RfiType[] = [];
  const rest = RfiTypes.filter((rfiType) => {
    if (rfiType.isDefault) {
      defaultRfiType = [rfiType];
      return false;
    }
    return true;
  });
  const alphabetically = (a: RfiType, b: RfiType) =>
    a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

  const sortedList = [...rest].sort(alphabetically);

  return [...defaultRfiType, ...sortedList];
};

/**
 * set isDefault to false for all types in the array.
 */
export const resetAllDefaultToFalse = (RfiTypes: RfiType[]) =>
  RfiTypes.map((rfiType) => ({ ...rfiType, isDefault: false }));

type OptimisticUpdateOnCreateSuccessArgs = {
  previousState: GetRfiTypesResponse;
  newRfiType: RfiType;
};

export const optimisticUpdateOnCreateSuccess = ({
  previousState,
  newRfiType,
}: OptimisticUpdateOnCreateSuccessArgs) => {
  const { isDefault } = newRfiType;
  const previousStateResults = previousState.results || [];
  const previousRfiTypes = isDefault
    ? resetAllDefaultToFalse(previousStateResults)
    : previousStateResults;

  const updatedRfiTypesResult = [...previousRfiTypes, newRfiType];
  const sortedRfiTypesResult = sortRfiTypesList(updatedRfiTypesResult);
  return sortedRfiTypesResult;
};

type OptimisticUpdateOnUpdateMutateArgs = {
  previousState: InfiniteData<GetRfiTypesResponse>;
  updatedRfiType: Partial<RfiType>;
  updatedRfiTypeId: string;
};

export const optimisticUpdateOnUpdateMutate = ({
  previousState,
  updatedRfiType,
  updatedRfiTypeId,
}: OptimisticUpdateOnUpdateMutateArgs) => {
  const { isDefault: shouldResetDefaults } = updatedRfiType;
  let allRfiTypes: RfiType[] = [];
  allRfiTypes = previousState.pages.flatMap(({ results }) => results);

  allRfiTypes = shouldResetDefaults ? resetAllDefaultToFalse(allRfiTypes) : allRfiTypes;

  const updatedRfiTypes = allRfiTypes.map((rfiType) => {
    if (rfiType.id !== updatedRfiTypeId) {
      return rfiType;
    }
    return { ...rfiType, ...updatedRfiType };
  });

  return {
    ...previousState,
    pages: [
      {
        results: sortRfiTypesList(updatedRfiTypes),
        pagination: {
          offset: 0,
          limit: updatedRfiTypes.length,
          totalResults: updatedRfiTypes.length,
        },
      },
    ],
  };
};
